import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Zoom,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";

// Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import LogoutIcon from "@mui/icons-material/Logout";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GridOnIcon from "@mui/icons-material/GridOn";
import * as XLSX from "xlsx";
import "jspdf-autotable";

import axios from "axios";
import { useNavigate } from "react-router-dom";

const COMPANY_URL = `${process.env.REACT_APP_API_URL}/api/company/admin`;

const CompanySuperDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const isMobile = useMediaQuery("(max-width:600px)");

  // ===== State =====
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState({
    name: "",
    username: "",
    password: "",
  });
  const [editIndex, setEditIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Settings Logic States
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState("Direct");
  const [loadingMode, setLoadingMode] = useState(false);

  const [formOpen, setFormOpen] = useState(false);

  // ===== White Theme with Yellow Accents =====
  const whiteCard = {
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: 0,
    color: "#000000",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  };

  const yellowText = { color: "#FFD600" };
  const yellowBorder = { borderColor: "#FFD600" };

  const whiteInput = {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
  };

  const yellowButton = {
    background: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      background: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  };

  const tableHeaderStyle = {
    color: "#000000",
    fontWeight: "bold",
    backgroundColor: "#f5f5f5",
    borderBottom: "2px solid #FFD600",
  };

  const tableCellStyle = {
    color: "#000000",
    borderBottom: "1px solid #e0e0e0",
  };

  // ===== Logic Handlers =====
  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchDepartments = useCallback(
    async (manual = false) => {
      try {
        const res = await axios.get(`${COMPANY_URL}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDepartments(Array.isArray(res.data.admins) ? res.data.admins : []);
        if (manual) showSnackbar("Table Updated", "success");
      } catch (err) {
        showSnackbar("Registry Sync Failed", "error");
      }
    },
    [token],
  );


  useEffect(() => {
    fetchDepartments(false);
  }, [fetchDepartments]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const downloadExcel = () => {
    try {
      const dataToExport = filteredDepartments.map((d) => ({
        "Admin Name": d.name,
        Username: d.username,
        Role: d.role,
        "Registry ID": d._id,
        "Sync Date": new Date().toLocaleString(),
      }));

      if (dataToExport.length === 0) {
        showSnackbar("No records found to export", "warning");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Admin_Registry");
      XLSX.writeFile(workbook, `ADMIN_REGISTRY_${new Date().getTime()}.xlsx`);
      showSnackbar("EXCEL_EXPORT_COMPLETE", "success");
    } catch (err) {
      showSnackbar("Excel Generation Failed", "error");
    }
  };

  // Filter and pagination logic
  const filteredDepartments = useMemo(() => {
    return departments.filter((d) =>
      d.username.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      d.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      d.role.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [departments, debouncedSearch]);

  const paginatedDepartments = useMemo(() => {
    return filteredDepartments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredDepartments, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      minHeight="100vh"
      sx={{
        backgroundColor: "#f5f5f5",
        py: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, px: 0 }}>
        {/* White Card */}
        <Paper sx={{ ...whiteCard, p: 0 }}>
          {/* Header Section */}
          <Box sx={{ p: 2.5, borderBottom: "1px solid #e0e0e0" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ border: "2px solid #FFD600", bgcolor: "#fff5e0", width: 40, height: 40 }}>
                  <AdminPanelSettingsIcon sx={{ color: "#FFD600" }} />
                </Avatar>
                <Typography variant="h6" fontWeight={900} sx={{ color: "#000000", letterSpacing: 1 }}>
                  COMPANY TERMINAL
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Export Excel">
                  <IconButton onClick={downloadExcel} sx={yellowButton}>
                    <GridOnIcon />
                  </IconButton>
                </Tooltip>
                {isMobile ? (
                  <Tooltip title="Logout">
                    <IconButton
                      onClick={() => {
                        localStorage.removeItem("token");
                        navigate("/login");
                      }}
                      sx={{
                        color: "#ff0000",
                        border: "1.5px solid #d60202",
                        bgcolor: "rgba(255, 0, 0, 0.05)",
                        transition: "0.3s",
                        "&:hover": {
                          color: "#ff0000",
                          border: "1px solid #ff4444",
                          bgcolor: "rgba(255, 0, 0, 0.1)",
                          boxShadow: "0 0 15px 2px rgba(255, 68, 68, 0.6)",
                        },
                      }}
                    >
                      <LogoutIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      localStorage.removeItem("token");
                      navigate("/login");
                    }}
                    startIcon={<LogoutIcon sx={{ color: "#ff0000" }} />}
                    sx={{
                      color: "#ff4444",
                      border: "1px solid #e0e0e0",
                      bgcolor: "rgba(255, 0, 0, 0.05)",
                      transition: "0.3s",
                      "&:hover": {
                        color: "#ff0000",
                        border: "1px solid #ff4444",
                        bgcolor: "rgba(255, 0, 0, 0.1)",
                        boxShadow: "0 0 15px 2px rgba(255, 68, 68, 0.6)",
                      },
                    }}
                  >
                    Logout
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Toolbar Section */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ width: "100%" }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  placeholder="Search..."
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={whiteInput}
                />
                <IconButton onClick={() => fetchDepartments(true)} sx={yellowButton}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Stack>
          </Box>

          {/* Table Section */}
          <Box sx={{ backgroundColor: "#ffffff" }}>
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 300px)",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f5f5f5",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#FFD600",
                  borderRadius: "4px",
                },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeaderStyle}>#</TableCell>
                    <TableCell sx={tableHeaderStyle}>Admin Name</TableCell>
                    <TableCell sx={tableHeaderStyle}>Username</TableCell>
                    <TableCell sx={tableHeaderStyle}>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedDepartments.map((d, index) => (
                    <TableRow
                      key={d._id}
                      sx={{
                        "&:hover": { backgroundColor: "#fafafa" },
                      }}
                    >
                      <TableCell sx={tableCellStyle}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={tableCellStyle}>{d.name}</TableCell>
                      <TableCell sx={tableCellStyle}>{d.username}</TableCell>
                      <TableCell sx={tableCellStyle}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: d.role === "COMPANY_SUPER" ? "#FFD600" : "#000000",
                            fontWeight: "bold"
                          }}
                        >
                          {d.role}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableCellStyle}>
                        <Stack direction="row" spacing={0.5}>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedDepartments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ ...tableCellStyle, textAlign: "center", py: 5 }}>
                        NO REGISTRY FOUND
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredDepartments.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                color: "#000000",
                borderTop: "1px solid #e0e0e0",
                "& .MuiTablePagination-selectIcon": { color: "#FFD600" },
                "& .MuiIconButton-root": { color: "#FFD600" },
                "& .MuiTablePagination-select": { color: "#000000" },
                "& .MuiTypography-root": { color: "#000000" },
              }}
            />
          </Box>
        </Paper>

      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert variant="filled" severity={snackbar.severity} sx={{ bgcolor: "#ffffff", color: "#000000", border: "1px solid #FFD600" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanySuperDashboard;